import { CogoMark } from "../components/cogo-mark";
import { COOGO_IDE_CHROME_COPY } from "../coogo-ide-chrome-copy";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import type {
  StudioCockpitTab,
  StudioCockpitTabState,
} from "./studio-cockpit-activity";

interface CockpitTabSignalProps {
  tab: StudioCockpitTab;
  label: string;
  selected: boolean;
  state: StudioCockpitTabState;
}

const TONE_CLASS: Record<StudioCockpitTabState["kind"], string> = {
  idle: "text-muted-foreground",
  working: "text-primary",
  changed: "text-primary",
  attention: "text-amber-600",
  blocked: "text-destructive",
  completed: "text-success",
};

export function CockpitTabSignal({
  label,
  selected,
  state,
}: CockpitTabSignalProps) {
  // I3 — both names below were assembled at this call site: the status one
  // spliced a localized tab label onto the raw engine token (`앱: working`),
  // and the badge one was hard-coded English with a broken plural
  // ("1 unreviewed changes"). Both phrases are now authored per language.
  const { locale } = useStudioLocale();
  const copy = COOGO_IDE_CHROME_COPY[locale].cockpitTabSignal;
  if (selected || (state.kind === "idle" && state.unreadCount === 0)) {
    return null;
  }

  const visibleCount =
    state.unreadCount > 99 ? "99+" : String(state.unreadCount);

  return (
    <span
      className={`flex shrink-0 items-center gap-0.5 ${TONE_CLASS[state.kind]}`}
      role="status"
      aria-label={copy.status(label, state.kind)}
      data-cockpit-tab-signal
    >
      {state.kind !== "idle" && (
        <span
          data-testid="cockpit-chevron-signal"
          data-motion={state.kind === "working" ? "working" : "static"}
          className={
            state.kind === "working"
              ? "motion-safe:animate-pulse motion-reduce:animate-none"
              : undefined
          }
          aria-hidden
        >
          <CogoMark size={16} variant="speed" />
        </span>
      )}
      {state.unreadCount > 0 && (
        <span
          aria-label={copy.unreviewed(state.unreadCount)}
          className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-current px-1 text-[9px] font-bold leading-none text-background"
        >
          {visibleCount}
        </span>
      )}
    </span>
  );
}
